import type { FormikHelpers } from 'formik'

import { Formik } from 'formik'

import { NumberFormatInputWithLabel, SelectInputWithLabel } from 'components'
import { PRESCREEN_FIELDS, PRESCREEN_FIELD_KEYS, PRESCREEN_LEASE_TERM_OPTIONS } from 'constant'

import { PrescreenStyled, SecondaryButton, PrimaryButton } from './prescreen-form.style'

export type PrescreenFieldKeys = keyof typeof PRESCREEN_FIELDS
export type PrescreenFields = Partial<Record<PrescreenFieldKeys, string>>
export type PrescreenFormProps = {
  value?: PrescreenFields
  onSubmit: (formData: PrescreenFields) => void
  onClickPrevious: () => void
  submitText?: string
}

const initialValues: PrescreenFields = PRESCREEN_FIELD_KEYS.reduce((acc, key) => {
  // eslint-disable-next-line no-param-reassign
  acc[key] = ''
  return acc
}, {})

export const PrescreenForm: React.FC<PrescreenFormProps> = ({
  value,
  onSubmit,
  onClickPrevious,
  submitText = 'Send Message',
}) => {
  const handleOnClickPrevious = () => {
    /* eslint no-unused-expressions: ["error", { "allowShortCircuit": true }] */
    onClickPrevious && onClickPrevious()
  }

  const handleSubmit = (
    formData: PrescreenFields,
    { setSubmitting }: FormikHelpers<PrescreenFields>,
  ) => {
    setSubmitting(true)
    onSubmit(formData)
  }

  return (
    <Formik initialValues={{ ...initialValues, ...value }} onSubmit={handleSubmit}>
      {({ handleBlur, setFieldValue, values, errors, touched, isSubmitting }) => (
        <PrescreenStyled>
          <NumberFormatInputWithLabel
            id="moveInDate"
            name="moveInDate"
            label={PRESCREEN_FIELDS.moveInDate.label}
            placeholder={PRESCREEN_FIELDS.moveInDate.placeholder}
            type="date"
            inputMode="numeric"
            onChange={(target) => {
              setFieldValue('moveInDate', target.formattedValue)
            }}
            onBlur={handleBlur}
            value={values?.moveInDate}
            error={touched?.moveInDate && errors?.moveInDate}
            disabled={isSubmitting}
          />
          <SelectInputWithLabel
            id="preferredLeaseTerm"
            name="preferredLeaseTerm"
            label={PRESCREEN_FIELDS.preferredLeaseTerm.label}
            placeholder={PRESCREEN_FIELDS.preferredLeaseTerm.placeholder}
            options={PRESCREEN_LEASE_TERM_OPTIONS}
            onChange={(target) => {
              setFieldValue('preferredLeaseTerm', target)
            }}
            value={values?.preferredLeaseTerm}
            error={touched?.preferredLeaseTerm && errors?.preferredLeaseTerm}
            disabled={isSubmitting}
          />
          <NumberFormatInputWithLabel
            id="householdMonthlyIncome"
            name="householdMonthlyIncome"
            label={PRESCREEN_FIELDS.householdMonthlyIncome.label}
            placeholder={PRESCREEN_FIELDS.householdMonthlyIncome.placeholder}
            type="number"
            inputMode="numeric"
            onChange={(target) => {
              setFieldValue('householdMonthlyIncome', target.value)
            }}
            onBlur={handleBlur}
            value={values?.householdMonthlyIncome}
            error={touched?.householdMonthlyIncome && errors?.householdMonthlyIncome}
            disabled={isSubmitting}
          />
          <NumberFormatInputWithLabel
            id="occupy"
            name="occupy"
            label={PRESCREEN_FIELDS.occupy.label}
            placeholder={PRESCREEN_FIELDS.occupy.placeholder}
            type="number"
            inputMode="numeric"
            onChange={(target) => {
              setFieldValue('occupy', target.value)
            }}
            onBlur={handleBlur}
            value={values?.occupy}
            error={touched?.occupy && errors?.occupy}
            disabled={isSubmitting}
          />
          <PrimaryButton
            id="submitPrescreenFormButton"
            type="submit"
            loading={isSubmitting}
            data-testid="schedule-dispatch-btn"
          >
            {submitText}
          </PrimaryButton>
          <SecondaryButton
            id="goPreviousButton"
            onClick={handleOnClickPrevious}
            disabled={isSubmitting}
            as="button"
          >
            Previous
          </SecondaryButton>
        </PrescreenStyled>
      )}
    </Formik>
  )
}
